import React, { useState } from 'react';
import { pokemonData } from './data/pokemon';
import { getSuperEffectiveTypes, getWeakTypes, typeEffectiveness } from './data/typeEffectiveness';
import TypeBadge from './components/TypeBadge';
import SearchBar from './components/SearchBar';

function App() {
  const [pokemon, setPokemon] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');

  const handleSearch = (searchValue) => {
    const searchTerm = searchValue.trim().toLowerCase();
    setLastSearchTerm(searchValue.trim());
    
    if (!searchTerm) {
      setPokemon(null);
      setNotFound(false);
      return;
    }

    const foundPokemon = pokemonData.find(
      p => p.name.toLowerCase() === searchTerm
    );

    if (foundPokemon) {
      setPokemon(foundPokemon);
      setNotFound(false);
    } else {
      setPokemon(null);
      setNotFound(true);
    }
  };

  const getSuperEffective = (types) => {
    return getSuperEffectiveTypes(types);
  };

  const getWeakAgainst = (types) => {
    return getWeakTypes(types);
  };

  const getStrongAgainstByType = (pokemonTypes) => {
    const strongAgainst = {};
    
    pokemonTypes.forEach(type => {
      const superEffective = [];
      // Look at what this type is strong against
      for (const targetType in typeEffectiveness[type]) {
        if (typeEffectiveness[type][targetType] > 1) {
          superEffective.push(targetType);
        }
      }
      strongAgainst[type] = superEffective;
    });
    
    return strongAgainst;
  };

  const getResistantTo = (pokemonTypes) => {
    const resistant = [];
    
    // Look at what types are not very effective against this Pokémon
    for (const attackingType in typeEffectiveness) {
      let effectiveness = 1;
      for (const defendingType of pokemonTypes) {
        effectiveness *= typeEffectiveness[attackingType][defendingType];
      }
      if (effectiveness < 1 && effectiveness > 0) {
        resistant.push(attackingType);
      }
    }
    
    return resistant;
  };

  const getImmuneTo = (pokemonTypes) => {
    const immune = [];
    
    // Look at what types are completely ineffective against this Pokémon
    for (const attackingType in typeEffectiveness) {
      let effectiveness = 1;
      for (const defendingType of pokemonTypes) {
        effectiveness *= typeEffectiveness[attackingType][defendingType];
      }
      if (effectiveness === 0) {
        immune.push(attackingType);
      }
    }
    
    return immune;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">BattleDex</h1>
          </div>
          <p className="text-gray-600 text-lg">Gen 3 Pokémon Type Calculator</p>
        </div>

        {/* Search Form */}
        <div className="mb-8">
          <SearchBar
            onSearch={handleSearch}
            placeholder="Enter a Gen 3 Pokémon name (e.g., Torchic, Blaziken, Rayquaza)"
          />
        </div>

        {/* Results */}
        {pokemon && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
            {/* Pokémon Info */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="text-sm font-mono text-gray-500">#{pokemon.id.toString().padStart(3, '0')}</span>
              </div>
              <div className="mb-4">
                <img
                  src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemon.id}.png`}
                  alt={pokemon.name}
                  className="w-32 h-32 mx-auto bg-gray-100 rounded-full p-2 border-2 border-gray-200"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <h2 className="text-3xl font-bold mb-2">
                <a
                  href={`https://serebii.net/pokedex-rs/${pokemon.id.toString().padStart(3, '0')}.shtml`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  title="View on Serebii"
                >
                  {pokemon.name}
                </a>
              </h2>
              <div className="flex justify-center gap-2">
                {pokemon.types.map((type) => (
                  <TypeBadge key={type} type={type} />
                ))}
              </div>
            </div>

            {/* Type Effectiveness */}
            <div className="space-y-6">
              {/* Weak Against */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  Weak Against
                </h3>
                <div className="flex flex-wrap gap-2">
                  {getSuperEffective(pokemon.types).length > 0 ? (
                    getSuperEffective(pokemon.types).map((type) => (
                      <TypeBadge key={type} type={type} variant="strong" />
                    ))
                  ) : (
                    <p className="text-red-600 italic">No weaknesses</p>
                  )}
                </div>
              </div>

              {/* Resistant To */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  Resistant To
                </h3>
                <div className="flex flex-wrap gap-2">
                  {getResistantTo(pokemon.types).length > 0 ? (
                    getResistantTo(pokemon.types).map((type) => (
                      <TypeBadge key={type} type={type} variant="resistant" />
                    ))
                  ) : (
                    <p className="text-blue-600 italic">No resistances</p>
                  )}
                </div>
              </div>

              {/* Immune To */}
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  Immune To
                </h3>
                <div className="flex flex-wrap gap-2">
                  {getImmuneTo(pokemon.types).length > 0 ? (
                    getImmuneTo(pokemon.types).map((type) => (
                      <TypeBadge key={type} type={type} variant="immune" />
                    ))
                  ) : (
                    <p className="text-purple-600 italic">No immunities</p>
                  )}
                </div>
              </div>

              {/* Strong Against */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  Strong Against
                </h3>
                <div className="space-y-3">
                  {(() => {
                    const strongAgainstByType = getStrongAgainstByType(pokemon.types);
                    const allStrongAgainst = getWeakAgainst(pokemon.types);
                    
                    if (allStrongAgainst.length === 0) {
                      return <p className="text-green-600 italic">No super-effective matchups</p>;
                    }
                    
                    return pokemon.types.map((pokemonType) => {
                      const strongTypes = strongAgainstByType[pokemonType];
                      if (strongTypes.length === 0) return null;
                      
                      return (
                        <div key={pokemonType} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <TypeBadge type={pokemonType} />
                            <span className="text-sm text-green-700 font-medium">→</span>
                          </div>
                          <div className="flex flex-wrap gap-2 ml-6">
                            {strongTypes.map((type) => (
                              <TypeBadge key={type} type={type} variant="weak" />
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Not Found Message */}
        {notFound && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg text-center">
            <div className="text-6xl mb-4">😢</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Pokémon Not Found</h3>
            <p className="text-gray-600">
              "{lastSearchTerm}" wasn't found in the Gen 3 Pokédex. 
              Make sure you're searching for a Pokémon from Ruby, Sapphire, or Emerald.
            </p>
          </div>
        )}

        {/* Instructions */}
        {!pokemon && !notFound && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg text-center">
            <div className="text-6xl mb-4">⚡</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Ready to Battle!</h3>
            <p className="text-gray-600 mb-4">
              Search for any Gen 3 Pokémon to see their type effectiveness.
            </p>
            <div className="text-sm text-gray-500">
              <p>Try searching for: Torchic, Blaziken, Rayquaza, or any other Gen 3 Pokémon!</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
